/**
 * Noise and the garrison alert (02 §6.3): "the alarm is about noise, not glimpses".
 * Footsteps, shouts, gunfire and blasts are all sounds in the world; what hears them reacts,
 * and only gunfire and blasts raise the alert for the whole garrison.
 */

import { type Ctx, isFighter } from './ctx';
import { ALERT, ENEMY, secs, TACTICS } from './tuning';
import type { BarkKind, Enemy, MissionDef } from './types';
import { muffle } from './weather';

export const LOUD_BARKS: ReadonlySet<BarkKind> = new Set<BarkKind>(['contact', 'manDown', 'alarm', 'help']);

/**
 * How long the garrison takes to get its first wave moving once the alarm has gone up for good.
 *
 * Jammed Radios (04 §8) promised "reinforcements are delayed by 60 s" for 8 RP and nothing in the sim
 * read the card at all (ROUND5). The minute is added to the FIRST wave only: the barracks cannot be
 * called, and once the runner has got through, the waves that follow come at the usual spacing - which
 * still puts every one of them a minute later than it would have been. Pure, and read from the mission
 * exactly as `radioSilent` and `hotPickup` read a modifier.
 *
 * The minute is here rather than in `ALERT` because `tuning.ts` is at the 300-line limit to the line,
 * and the fix for that is to split it by concern, which is not this round's job.
 */
const JAMMED_DELAY_TICKS = secs(60);

export const firstWaveTicks = (def: MissionDef): number =>
  ALERT.firstWaveTicks + (def.intel.includes('jammed-radios') ? JAMMED_DELAY_TICKS : 0);

/**
 * A soldier calls out. Barks are presentation (a glyph, later a voice) AND simulation:
 * they are real noise that brings nearby men over - though a shout is never an alarm.
 */
export function bark(ctx: Ctx, e: Enemy, kind: BarkKind): void {
  if (e.barkTicks > 0 && kind !== 'alarm' && kind !== 'grenade') return;
  e.barkTicks = TACTICS.barkCooldown;
  ctx.events.push({ type: 'bark', id: e.id, x: e.x, z: e.z, kind });
  if (kind === 'allClear' || kind === 'suppressed') return;
  makeNoise(ctx, e.x, e.z, LOUD_BARKS.has(kind) ? TACTICS.barkRadius : TACTICS.quietBarkRadius, 'shout', e.id);
}

export function raiseAlert(ctx: Ctx, level: number, x: number, z: number): void {
  const w = ctx.world;
  w.alertQuietTicks = 0;
  w.disturbanceX = x;
  w.disturbanceZ = z;
  if (level > w.alert) {
    w.alert = level;
    w.maxAlert = Math.max(w.maxAlert, level);
    ctx.events.push({ type: 'alert', level });
    if (level >= 3) w.reinforcementTicks = firstWaveTicks(ctx.statics.def);
  }
}

/** Send an enemy to look at a place, unless he is already fighting or out of action. */
export function sendToInvestigate(e: Enemy, x: number, z: number, urgent: boolean): void {
  if (!isFighter(e) || e.state === 'down' || e.state === 'combat' || e.mans !== null) return;
  if (e.state === 'runner' || e.state === 'flee' || e.state === 'rescue') return;
  e.state = 'investigate';
  e.targetX = x;
  e.targetZ = z;
  e.path = [];
  e.pathIndex = 0;
  e.stateTicks = 0;
  if (urgent) e.detection = Math.max(e.detection, 0.5);
}

/**
 * A sound in the world. Living enemies inside `radius` react; gunfire and explosions also
 * raise the garrison alert if anyone at all heard them.
 */
export function makeNoise(
  ctx: Ctx,
  x: number,
  z: number,
  radius: number,
  kind: 'step' | 'shout' | 'gunfire' | 'blast',
  sourceId = -1,
): void {
  // R5: the 0.7 is `NOISE.arctic` now, so the HUD's noise ring can muffle by the same figure the
  // garrison hears by instead of drawing every footstep 30 % too wide on snow (ROUND5 §3, last bullet).
  const r = radius * muffle(ctx.statics.def.biome);
  let heard = false;
  for (const e of ctx.world.enemies) {
    if (!isFighter(e) || e.state === 'down' || e.id === sourceId) continue;
    const dx = e.x - x;
    const dz = e.z - z;
    if (dx * dx + dz * dz > r * r) continue;
    heard = true;
    if (kind === 'step') {
      if (e.state === 'combat') continue;
      e.detection = Math.min(0.95, e.detection + 0.14);
      if (e.detection >= ENEMY.suspiciousAt && (e.state === 'guard' || e.state === 'patrol' || e.state === 'return')) {
        e.state = 'suspicious';
        e.stateTicks = ENEMY.suspiciousTicks;
        e.targetX = x;
        e.targetZ = z;
      }
    } else if (kind === 'shout' || e.duty === 'patrol' || dx * dx + dz * dz <= 25 * 25) {
      sendToInvestigate(e, x, z, true);
    } else {
      // A sentry with a post to hold stays on it, but he is listening now.
      e.wary = true;
      e.detection = Math.max(e.detection, 0.2);
    }
  }
  if (!heard) return;
  if (kind === 'gunfire') {
    ctx.world.combatTicks += 12;
    raiseAlert(ctx, ctx.world.combatTicks >= ALERT.sustainedCombatTicks ? 3 : 2, x, z);
  } else if (kind === 'blast') {
    raiseAlert(ctx, 2, x, z);
  }
  if (kind === 'gunfire' || kind === 'blast') {
    // At alert 2+ everyone on patrol within reach converges on the disturbance.
    for (const e of ctx.world.enemies) {
      if (e.duty !== 'patrol') continue;
      const dx = e.x - x;
      const dz = e.z - z;
      if (dx * dx + dz * dz <= ALERT.convergeRadius * ALERT.convergeRadius) sendToInvestigate(e, x, z, false);
    }
  }
}
